import React from "react"
import { composeSx } from "../theme/composeSx"
import { Link, type LinkProps, type Theme } from "@mui/material"
import { resolveViraPalette } from "../theme/resolveViraPalette"

export type ViraLinkVariant = "default" | "nav" | "subtle" | "underline"

export type ViraLinkProps = Omit<LinkProps, "variant"> & {
  variant?: ViraLinkVariant
  active?: boolean
  disabled?: boolean
}

function variantSx(
  theme: Theme,
  variant: ViraLinkVariant,
  active: boolean,
): object {
  const vira = resolveViraPalette(theme)
  switch (variant) {
    case "default":
      return {
        fontSize: 12.5,
        fontWeight: 500,
        color: vira.lavenderText,
        cursor: "pointer",
      }
    case "nav":
      return {
        fontSize: 13.5,
        fontWeight: active ? 600 : 400,
        color: active ? vira.ink : vira.muted,
        cursor: "pointer",
      }
    case "subtle":
      return {
        fontSize: 12,
        color: vira.subtle,
        cursor: "pointer",
      }
    case "underline":
      return {
        fontSize: 12,
        fontWeight: 600,
        color: vira.lavenderText,
        textDecoration: "underline",
        cursor: "pointer",
      }
  }
}

/** Presentational text link (button or anchor). Alias: TextLink. */
export function ViraLink({
  variant = "default",
  active = false,
  disabled = false,
  underline = "none",
  component = "button",
  sx,
  onClick,
  href,
  tabIndex,
  children,
  ...rest
}: ViraLinkProps) {
  const isNativeButton = component === "button"

  return (
    <Link
      component={component}
      underline={underline}
      href={disabled ? undefined : href}
      tabIndex={disabled ? -1 : tabIndex}
      // Native disabled only applies to button; anchors omit href + block activation.
      {...(isNativeButton ? { disabled } : {})}
      onClick={
        disabled
          ? (event: React.MouseEvent<HTMLElement>) => {
              event.preventDefault()
              event.stopPropagation()
            }
          : onClick
      }
      onKeyDown={
        disabled
          ? (event: React.KeyboardEvent<HTMLElement>) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault()
                event.stopPropagation()
              }
            }
          : undefined
      }
      aria-disabled={disabled || undefined}
      sx={composeSx((theme) => ({
        ...variantSx(theme, variant, active),
        ...(disabled
          ? { opacity: 0.45, cursor: "not-allowed", pointerEvents: "none" }
          : null)}), sx)}
      {...rest}
    >
      {children}
    </Link>
  )
}

export const TextLink = ViraLink
